import { ROLE_PRIORITY, Role, RolesApiService } from '@angular-workspace/services';

export type MahaPanel = 'admin' | 'org-admin' | 'provider' | 'consumer';

export const MAHA_AGX_PROTECTED_PANELS = [
  '/consumer',
  '/provider',
  '/org-admin',
  '/admin',
] as const;

const ATTEMPTED_PATH_KEY = 'mahaagx-login-attempted-path';

export function getMahaAgxPanelKeyFromPath(url: string): MahaPanel | null {
  const p = url.split('?')[0];
  if (p.startsWith('/admin')) return 'admin';
  if (p.startsWith('/org-admin')) return 'org-admin';
  if (p.startsWith('/provider')) return 'provider';
  if (p.startsWith('/consumer')) return 'consumer';
  return null;
}

export function mahaPanelToRole(panel: MahaPanel | null): Role | null {
  switch (panel) {
    case 'admin':
      return Role.ADMIN;
    case 'org-admin':
      return Role.ORG_ADMIN;
    case 'provider':
      return Role.PROVIDER;
    case 'consumer':
      return Role.CONSUMER;
    default:
      return null;
  }
}

/** True when Keycloak/user-info includes a panel role the app can route to. */
export function hasRecognizedMahaPanelRole(rolesService: RolesApiService): boolean {
  return rolesService.getHighestRole(rolesService.getUserRoles()) != null;
}

export function normalizeMahaAgxRoutePath(pathOrUrl: string): string {
  if (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://')) {
    const url = new URL(pathOrUrl);
    return url.pathname + url.search;
  }
  return pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
}

export function persistLoginAttemptedPath(pathOrUrl: string): string {
  const path = normalizeMahaAgxRoutePath(pathOrUrl);
  sessionStorage.setItem(ATTEMPTED_PATH_KEY, path);
  return path;
}

export function getLoginAttemptedPath(): string | undefined {
  return sessionStorage.getItem(ATTEMPTED_PATH_KEY) ?? undefined;
}

export function clearLoginAttemptedPath(): void {
  sessionStorage.removeItem(ATTEMPTED_PATH_KEY);
}

/** True when the user's highest role outranks the panel for the given URL. */
export function isHigherRoleThanPanel(
  panel: MahaPanel,
  rolesService: RolesApiService
): boolean {
  const userRoles = rolesService.getUserRoles();
  if (!userRoles.length) {
    return false;
  }
  const highestRole = rolesService.getHighestRole(userRoles);
  if (!highestRole) {
    return false;
  }
  const highestP = ROLE_PRIORITY[highestRole] ?? 0;
  const panelP = ROLE_PRIORITY[panel] ?? 0;
  return highestP > panelP;
}

/**
 * @returns Panel home route when user must leave a lower panel, otherwise `null`.
 */
export function getRedirectRouteForHigherRoleUser(
  url: string,
  rolesService: RolesApiService
): string | null {
  const panel = getMahaAgxPanelKeyFromPath(url);
  if (!panel || !isHigherRoleThanPanel(panel, rolesService)) {
    return null;
  }
  return rolesService.getHighestRolePanelRoute(rolesService.getUserRoles());
}

/**
 * Decide where to send the user after login when they originally hit a protected panel URL.
 * @returns Route to navigate to, or `null` to stay on catalogue home.
 */
export function resolvePostLoginRoute(
  attemptedPath: string,
  rolesService: RolesApiService
): string | null {
  const panel = getMahaAgxPanelKeyFromPath(attemptedPath);
  if (!panel) {
    return null;
  }

  const userRoles = rolesService.getUserRoles();
  if (!userRoles.length) {
    return null;
  }

  const highestRole = rolesService.getHighestRole(userRoles);
  if (!highestRole) {
    return null;
  }

  const highestP = ROLE_PRIORITY[highestRole] ?? 0;
  const attemptedP = ROLE_PRIORITY[panel] ?? 0;

  if (highestP > attemptedP) {
    return rolesService.getHighestRolePanelRoute(userRoles);
  }

  return attemptedPath;
}
